"use client";
import React, { useEffect, useState, useRef, useCallback } from "react";

interface BusinessSectorsListProps {
    items: string[];
    separator?: string;
    spanClassName?: string;
    gap?: number;
}

export const Subtitle: React.FC<BusinessSectorsListProps> = ({
    items = [],
    separator = "•",
    spanClassName = "",
    gap = 2
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLSpanElement | null)[]>([]);
    const rafRef = useRef<number | null>(null);

    const [mounted, setMounted] = useState(false);
    const [hasCalculated, setHasCalculated] = useState(false);
    // One entry per gap between items (items.length - 1). true = show separator.
    // Pre-calc: treat all as visible so layout reserves maximum space.
    // Post-calc: only pairs measured as same-line remain visible.
    const [separatorVisibility, setSeparatorVisibility] = useState<boolean[]>([]);

    useEffect(() => {
        setMounted(true);
    }, []);

    const calculate = useCallback(() => {
        if (!containerRef.current) return;
        if (itemRefs.current.length !== items.length) return;
        if (itemRefs.current.some(ref => ref === null)) return;

        const next: boolean[] = [];
        for (let i = 0; i < items.length - 1; i++) {
            const a = itemRefs.current[i];
            const b = itemRefs.current[i + 1];
            if (!a || !b) {
                next.push(false);
                continue;
            }
            const ra = a.getBoundingClientRect();
            const rb = b.getBoundingClientRect();
            const ca = ra.top + ra.height / 2;
            const cb = rb.top + rb.height / 2;
            next.push(Math.abs(ca - cb) < 5);
        }

        setSeparatorVisibility(prev => {
            if (prev.length === next.length && prev.every((v, i) => v === next[i])) {
                return prev;
            }
            return next;
        });
        setHasCalculated(true);
    }, [items]);

    const scheduleCalculate = useCallback(() => {
        if (rafRef.current !== null) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            calculate();
        });
    }, [calculate]);

    useEffect(() => {
        if (!mounted) return;
        scheduleCalculate();
        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [mounted, scheduleCalculate]);

    return (
        <div id="businessSectors" className="business-sectors-container min-h-10">
            <div
                ref={containerRef}
                className="business-sectors-list"
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: `${gap}px`,
                    justifyContent: 'center',
                    alignItems: 'center',
                    lineHeight: 'inherit',
                    // Fade in only after the first measurement completes so
                    // users don't see separators flash in the wrong positions.
                    opacity: hasCalculated ? 1 : 0,
                    transition: 'opacity 300ms ease-in-out'
                }}
            >
                {items.map((item, index) => {
                    // Before calc: show ALL separators (widest layout).
                    // After calc: show only separators between same-line pairs.
                    // This direction guarantees post-calc layout is <= pre-calc layout,
                    // so no pair ever widens and causes a new wrap.
                    const isLast = index === items.length - 1;
                    const showSep = !isLast && (!hasCalculated || separatorVisibility[index] === true);
                    return (
                        <React.Fragment key={index}>
                            <span
                                ref={(el) => { itemRefs.current[index] = el; }}
                                className={`business-sector-item whitespace-nowrap ${spanClassName}`}
                                style={{ display: 'inline-block' }}
                            >
                                {item}
                            </span>
                            {!isLast && (
                                <span
                                    className={`business-sector-separator ${spanClassName}`}
                                    style={{
                                        userSelect: 'none',
                                        display: showSep ? 'inline-block' : 'none'
                                    }}
                                >
                                    {separator}
                                </span>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default Subtitle;